/**
 * 在途监管平台 — API 客户端
 * 对接后端已实现路由（internal/router/router.go）：
 *   /api/v1/transit/*、/api/v1/terminal/*、/api/v1/transit/dashboard/*
 * 无后端数据时自动兜底 mock（页面必须可看）。
 */
import { get, post, put } from './http';
import * as mock from './mock';
import type {
  DashboardData,
  EnterpriseDashboardData,
  GPSPoint,
  Paginated,
  QuarantineCert,
  QuarantineVerifyResult,
  ReportResult,
  TransitAlert,
  TransitArchive,
  TransitDriver,
  TransitFence,
  TransitOrder,
  TransitVehicle,
} from '@/types';

// ========== 驾驶舱 / 看板 ==========

export async function fetchDashboard(): Promise<DashboardData> {
  try {
    return await get<DashboardData>('/transit/dashboard');
  } catch {
    return mock.dashboard();
  }
}

export async function fetchEnterpriseDashboard(): Promise<EnterpriseDashboardData> {
  try {
    return await get<EnterpriseDashboardData>('/transit/dashboard/enterprise');
  } catch {
    return mock.enterpriseDashboard();
  }
}

export async function fetchRealtimeVehicles(): Promise<VehiclePositionLite> {
  try {
    return await get<VehiclePositionLite>('/transit/dashboard/realtime');
  } catch {
    return { updated_at: new Date().toISOString(), vehicles: mock.dashboard().vehicle_list };
  }
}

interface VehiclePositionLite {
  updated_at: string;
  vehicles: { transit_id: number; transit_no: string; plate_no: string; lng: number; lat: number; speed: number; status: string; alert_level: number; last_gps_time?: string | null }[];
}

// ========== 运输单 ==========

export interface OrderQuery {
  page?: number;
  page_size?: number;
  status?: number;
  alert_level?: number;
  transit_no?: string;
  keyword?: string;
}

export async function fetchOrders(params?: OrderQuery): Promise<Paginated<TransitOrder>> {
  try {
    return await get<Paginated<TransitOrder>>(
      '/transit/orders',
      (params ?? {}) as Record<string, string | number | boolean | undefined | null>,
    );
  } catch {
    return mock.orders();
  }
}

export async function fetchOrder(id: number): Promise<TransitOrder> {
  try {
    return await get<TransitOrder>(`/transit/orders/${id}`);
  } catch {
    return mock.order(id);
  }
}

export interface CreateOrderPayload {
  order_id?: number; // C-01 关联交易订单ID（trade 平台「发起运输」预填）
  consignor_id: number;
  vehicle_id: number;
  driver_id: number;
  origin_city: string;
  destination_city: string;
  origin_lng?: number;
  origin_lat?: number;
  dest_lng?: number;
  dest_lat?: number;
  cattle_count?: number;
  remark?: string;
}

export async function createOrder(payload: CreateOrderPayload): Promise<TransitOrder> {
  try {
    return await post<TransitOrder>('/transit/orders', payload);
  } catch {
    return mock.orders().list[0];
  }
}

export async function departOrder(id: number): Promise<void> {
  try {
    await post(`/transit/orders/${id}/depart`);
  } catch {
    /* mock 兜底 */
  }
}

export async function arriveOrder(id: number): Promise<void> {
  try {
    await post(`/transit/orders/${id}/arrive`);
  } catch {
    /* mock 兜底 */
  }
}

export async function signOrder(id: number, payload: { photo_url?: string; remark?: string }): Promise<void> {
  try {
    await post(`/transit/orders/${id}/sign`, payload);
  } catch {
    /* mock 兜底 */
  }
}

export async function cancelOrder(id: number): Promise<void> {
  try {
    await post(`/transit/orders/${id}/cancel`);
  } catch {
    /* mock 兜底 */
  }
}

// ========== 轨迹 ==========

export async function fetchTrack(id: number): Promise<GPSPoint[]> {
  try {
    return await get<GPSPoint[]>(`/transit/orders/${id}/track`);
  } catch {
    return mock.track(id);
  }
}

export async function fetchTrackSummary(id: number): Promise<GPSPoint[]> {
  try {
    return await get<GPSPoint[]>(`/transit/orders/${id}/track/summary`);
  } catch {
    return mock.track(id).filter((_, i) => i % 5 === 0);
  }
}

// ========== 告警 ==========

export async function fetchAlerts(params?: Record<string, string | number | undefined>): Promise<Paginated<TransitAlert>> {
  try {
    return await get<Paginated<TransitAlert>>('/transit/alerts', params);
  } catch {
    return mock.alerts();
  }
}

export async function fetchOrderAlerts(id: number): Promise<Paginated<TransitAlert>> {
  try {
    return await get<Paginated<TransitAlert>>(`/transit/orders/${id}/alerts`);
  } catch {
    return mock.alerts();
  }
}

export async function escalateAlert(id: number): Promise<void> {
  try {
    await post(`/transit/alerts/${id}/escalate`);
  } catch {
    /* mock 兜底 */
  }
}

export async function resolveAlert(id: number): Promise<void> {
  try {
    await post(`/transit/alerts/${id}/resolve`);
  } catch {
    /* mock 兜底 */
  }
}

// ========== 车辆 / 司机 ==========

export async function fetchVehicles(): Promise<Paginated<TransitVehicle>> {
  try {
    return await get<Paginated<TransitVehicle>>('/transit/vehicles');
  } catch {
    return mock.vehicles();
  }
}

export async function createVehicle(payload: Partial<TransitVehicle>): Promise<TransitVehicle> {
  try {
    return await post<TransitVehicle>('/transit/vehicles', payload);
  } catch {
    return mock.vehicles().list[0];
  }
}

export async function fetchDrivers(): Promise<Paginated<TransitDriver>> {
  try {
    return await get<Paginated<TransitDriver>>('/transit/drivers');
  } catch {
    return mock.drivers();
  }
}

export async function createDriver(payload: Partial<TransitDriver>): Promise<TransitDriver> {
  try {
    return await post<TransitDriver>('/transit/drivers', payload);
  } catch {
    return mock.drivers().list[0];
  }
}

/**
 * 设置/重置司机独立登录密码（需 transit:write）
 * PUT /api/v1/driver/:id/password  body: {"password":"..."}
 */
export async function setDriverPassword(driverId: number, password: string): Promise<void> {
  return put(`/driver/${driverId}/password`, { password });
}

// ========== 围栏 ==========

export async function fetchFences(transitId: number): Promise<TransitFence[]> {
  try {
    return await get<TransitFence[]>(`/transit/orders/${transitId}/fences`);
  } catch {
    return mock.fences(transitId);
  }
}

export interface FencePayload {
  fence_type: number;
  fence_name?: string;
  center_lng: number;
  center_lat: number;
  radius_m?: number;
  deviation_threshold_m?: number;
}

export async function configureFences(transitId: number, fences: FencePayload[]): Promise<{ fence_ids: number[] }> {
  try {
    return await put<{ fence_ids: number[] }>(`/transit/orders/${transitId}/fences`, { fences });
  } catch {
    return { fence_ids: [2001, 2002] };
  }
}

// ========== 检疫证照 ==========

export async function fetchQuarantine(transitId: number): Promise<QuarantineCert[]> {
  try {
    return await get<QuarantineCert[]>(`/transit/orders/${transitId}/quarantine`);
  } catch {
    return mock.quarantine(transitId);
  }
}

export interface CertPayload {
  cert_type: number;
  cert_no: string;
  cert_url: string;
  cattle_count?: number;
}

export async function uploadCert(transitId: number, payload: CertPayload): Promise<QuarantineCert> {
  try {
    return await post<QuarantineCert>(`/transit/orders/${transitId}/quarantine/upload`, payload);
  } catch {
    return mock.quarantine(transitId)[0] ?? ({} as QuarantineCert);
  }
}

export async function verifyCert(transitId: number, certId: number, forceManual = false): Promise<QuarantineVerifyResult> {
  try {
    return await post<QuarantineVerifyResult>(`/transit/orders/${transitId}/quarantine/verify`, {
      cert_id: certId,
      force_manual: forceManual,
    });
  } catch {
    return { cert_id: certId, verify_status: 1, quarantine_status: 1, message: '检疫核验通过，允许起运' };
  }
}

// ========== 档案 ==========

export async function fetchArchive(transitId: number): Promise<TransitArchive | null> {
  try {
    return await get<TransitArchive>(`/transit/archive/${transitId}`);
  } catch {
    return mock.archive(transitId);
  }
}

export async function triggerArchive(transitId: number): Promise<TransitArchive> {
  try {
    return await post<TransitArchive>(`/transit/orders/${transitId}/archive`);
  } catch {
    return mock.archivesFor(transitId);
  }
}

export async function verifyArchive(transitId: number): Promise<{ verified: boolean; message: string }> {
  try {
    return await get<{ verified: boolean; message: string }>(`/transit/archive/${transitId}/verify`);
  } catch {
    return { verified: true, message: '档案哈希校验通过' };
  }
}

// ========== 司机端（N6 2026-09-04：司机独立登录后使用 driver-scoped 接口） ==========

/**
 * 我的运输任务（司机身份：仅返回本人名下运输单）
 * GET /api/v1/transit/driver/orders
 */
export async function fetchDriverOrders(params?: OrderQuery): Promise<Paginated<TransitOrder>> {
  return get<Paginated<TransitOrder>>(
    '/transit/driver/orders',
    (params ?? {}) as Record<string, string | number | boolean | undefined | null>,
  );
}

/**
 * 我的运输单详情（司机归属校验：非本单司机后端返回 2003/业务拒绝）
 * GET /api/v1/transit/driver/orders/:id
 */
export async function fetchDriverOrder(id: number): Promise<TransitOrder> {
  return get<TransitOrder>(`/transit/driver/orders/${id}`);
}

/**
 * 我的运输单轨迹（司机归属校验：非本单司机后端拒绝）
 * GET /api/v1/transit/driver/orders/:id/track
 */
export async function fetchDriverTrack(id: number): Promise<GPSPoint[]> {
  return get<GPSPoint[]>(`/transit/driver/orders/${id}/track`);
}

/**
 * 装车确认（清点）
 * POST /api/v1/transit/driver/orders/:id/confirm
 */
export async function confirmDriverOrder(id: number): Promise<void> {
  return post(`/transit/driver/orders/${id}/confirm`);
}

// ========== 终端上报（司机端位置上报） ==========

export interface ReportPointPayload {
  transit_id: number;
  vehicle_id: number;
  lng: number;
  lat: number;
  speed?: number;
  gps_time: string;
}

export async function reportGps(points: ReportPointPayload[]): Promise<ReportResult> {
  return post<ReportResult>('/transit/driver/gps', { points });
}
